import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import NavBar from "../../Admin/Dashboard/NavBar";
import ReactProSidebar from "../../Admin/Dashboard/ReactProSidebar";
import "./../../../styles/App.css";
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Clock3,
  FileAudio2,
  FileUp,
  FileVideo2,
  Languages,
  Link2,
  Loader2,
  MessageSquareText,
  Play,
  RefreshCw,
  Sparkles,
  UserRound,
  X,
} from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faClock,
  faLanguage,
  faCalendar,
  faMicrochip,
  faTriangleExclamation,
  faCircleCheck,
  faCircleUser,
  faMessage,
  faFileVideo,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import {
  faYoutube,
  faInstagram,
  faTiktok,
  faXTwitter,
  faFacebook,
} from "@fortawesome/free-brands-svg-icons";
import "./AudioAnalysisModule.css";
import { modelsProxyUrl } from "../../../API/axiosInstance";

const DEFAULT_PERSIST_KEY = "csmart-audio-analysis-module";

const statusConfig = {
  processing: {
    label: "Processing",
    icon: Clock3,
  },
  completed: {
    label: "Completed",
    icon: CheckCircle2,
  },
  failed: {
    label: "Failed",
    icon: AlertCircle,
  },
};

let youtubeApiPromise = null;

function createClientId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `analysis-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isObject(value) {
  return typeof value === "object" && value !== null;
}

function nowIso() {
  return new Date().toISOString();
}

function progressSeed() {
  return Math.floor(14 + Math.random() * 12);
}

function safeParseStoredState(persistKey) {
  if (!persistKey || typeof window === "undefined") {
    return { analyses: [], details: {} };
  }
  try {
    const parsed = JSON.parse(window.localStorage.getItem(persistKey) || "{}");
    return {
      analyses: Array.isArray(parsed.analyses) ? parsed.analyses : [],
      details: isObject(parsed.details) ? parsed.details : {},
    };
  } catch {
    return { analyses: [], details: {} };
  }
}

function saveStoredState(persistKey, analyses, details) {
  if (!persistKey || typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(
      persistKey,
      JSON.stringify({ analyses, details }),
    );
  } catch {
    /* Storage can be unavailable in private windows. The module still works in memory. */
  }
}

function formatTimestamp(seconds) {
  if (!Number.isFinite(seconds)) {
    return "0:00";
  }
  const safeSeconds = Math.max(0, seconds);
  const mins = Math.floor(safeSeconds / 60);
  const secs = Math.floor(safeSeconds % 60);
  const tenths = Math.floor((safeSeconds % 1) * 10);
  return `${mins}:${secs.toString().padStart(2, "0")}.${tenths}`;
}

function formatDateTime(iso) {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return "Just now";
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function getYouTubeId(url) {
  if (!url) {
    return undefined;
  }
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\.|^m\./, "");
    if (host === "youtu.be") {
      const id = parsed.pathname.slice(1);
      return id || undefined;
    }
    if (host === "youtube.com" || host.endsWith(".youtube.com")) {
      const v = parsed.searchParams.get("v");
      if (v) {
        return v;
      }
      const match = parsed.pathname.match(/^\/(?:embed|shorts|v)\/([^/?#]+)/);
      if (match) {
        return match[1];
      }
    }
  } catch {
    return undefined;
  }
  return undefined;
}

function youtubeEmbedUrl(url) {
  const id = getYouTubeId(url);
  return id ? `https://www.youtube.com/embed/${id}` : undefined;
}

function youtubeThumbnailUrl(analysis) {
  if (analysis.thumbnail) {
    return analysis.thumbnail;
  }
  const id =
    getYouTubeId(analysis.video_url) || getYouTubeId(analysis.sourceUrl);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : undefined;
}

function isAudioUrl(url) {
  return Boolean(url && /\.(mp3|wav|m4a|aac|ogg|flac)(\?.*)?$/i.test(url));
}

function normalizeStatus(rawStatus, hasTranscript) {
  if (
    rawStatus === "processing" ||
    rawStatus === "completed" ||
    rawStatus === "failed"
  ) {
    return rawStatus;
  }
  return hasTranscript ? "completed" : "processing";
}

function fallbackTitle(input) {
  if (input?.file?.name) {
    return input.file.name.replace(/\.[^.]+$/, "");
  }
  if (input?.url) {
    try {
      const parsed = new URL(input.url);
      return parsed.hostname.replace(/^www\./, "");
    } catch {
      return input.url;
    }
  }
  return "Untitled analysis";
}

function normalizeTranscript(segments) {
  if (!Array.isArray(segments)) {
    return [];
  }
  return segments
    .map((segment) => {
      if (!isObject(segment)) {
        return undefined;
      }
      return {
        speaker: String(segment.speaker || "Speaker"),
        start: Number(segment.start || 0),
        end: Number(segment.end || 0),
        text: String(segment.text || ""),
      };
    })
    .filter((segment) => Boolean(segment?.text));
}

function normalizeAnalysis(raw, clientId, input, existing) {
  const transcript = normalizeTranscript(raw.transcript);
  const hasTranscript = transcript.length > 0;
  const status = normalizeStatus(raw.status, hasTranscript);
  const id = String(raw.id || clientId);
  const videoUrl =
    raw.video_url ||
    raw.videoUrl ||
    input?.localVideoUrl ||
    input?.url ||
    existing?.video_url;

  const summary = {
    id,
    title: raw.title || existing?.title || fallbackTitle(input),
    language: raw.language || existing?.language || "Detecting",
    status,
    thumbnail: raw.thumbnail || existing?.thumbnail,
    video_url: videoUrl,
    uploader: raw.uploader || raw.uploader_name || existing?.uploader,
    sourceUrl: input?.url || existing?.sourceUrl,
    createdAt: existing?.createdAt || nowIso(),
    progress: status === "processing" ? existing?.progress || 12 : 100,
    error: raw.error || existing?.error,
  };

  return {
    summary,
    detail: {
      ...summary,
      transcript,
    },
  };
}

const AUDIO_ANALYZE_API =
  "https://limitation-apnic-cleanup-examinations.trycloudflare.com/audio-analyze";

async function analyzeMediaSSE(input, { onPing, onStatus, signal } = {}) {
  const hasFile = Boolean(input.file);

  let body;
  let headers;

  if (hasFile) {
    body = new FormData();
    body.append("file", input.file);
    body.append("type", "file");
  } else {
    body = JSON.stringify({ url: input.url });
    headers = { "Content-Type": "application/json" };
  }

  const response = await fetch(AUDIO_ANALYZE_API, {
    method: "POST",
    body,
    headers,
    signal,
  });

  // console.debug(
  //   "[audio-analyze] response",
  //   response.status,
  //   "content-type:",
  //   response.headers.get("content-type"),
  // );

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(text || "Request failed");
  }

  if (!response.body) {
    throw new Error("Response had no body");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = null;
  let currentDataLines = [];
  let lastPayload = null;

  const dispatch = () => {
    if (!currentDataLines.length) {
      currentEvent = null;
      return undefined;
    }
    const raw = currentDataLines.join("\n");
    const event = currentEvent;
    currentDataLines = [];
    currentEvent = null;

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = raw;
    }

    if (event === "ping") {
      onPing?.();
      return undefined;
    }
    if (event === "error") {
      throw new Error((isObject(parsed) && parsed.error) || "Server error");
    }
    if (event === "completed") {
      return isObject(parsed) ? parsed.result ?? parsed : parsed;
    }

    lastPayload = parsed;
    onStatus?.(parsed);
    return undefined;
  };

  try {
    for (let reading = true; reading; ) {
      const { done, value } = await reader.read();
      if (done) {
        reading = false;
        break;
      }

      // Some backends use CRLF line endings, so split on either.
      const chunk = decoder.decode(value, { stream: true });
      // console.debug("[audio-analyze] chunk", JSON.stringify(chunk));
      buffer += chunk;
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (line.startsWith("event:")) {
          currentEvent = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          currentDataLines.push(line.slice(5).trim());
        } else if (line === "") {
          const result = dispatch();
          if (result !== undefined) {
            return result;
          }
        }
      }
    }

    // Flush a trailing event that wasn't followed by a final blank line.
    const result = dispatch();
    if (result !== undefined) {
      return result;
    }
  } finally {
    reader.releaseLock();
  }

  // console.debug(
  //   "[audio-analyze] stream ended, leftover buffer:",
  //   JSON.stringify(buffer),
  //   "lastPayload:",
  //   lastPayload,
  // );

  // No explicit "completed" event was sent, but the connection closed after
  // delivering at least one payload (common for backends that stream plain
  // `data:` updates without `event:` framing) — treat the last one as final.
  if (isObject(lastPayload)) {
    return lastPayload.result ?? lastPayload;
  }

  throw new Error("Stream ended without a result");
}

async function getAnalysis(_apiBaseUrl, id, existing) {
  return {
    ...existing,
    id,
    transcript: existing?.transcript || [],
    status: existing?.status || "processing",
  };
}

function loadYouTubeIframeAPI() {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }
  if (window.YT && window.YT.Player) {
    return Promise.resolve();
  }
  if (youtubeApiPromise) {
    return youtubeApiPromise;
  }
  youtubeApiPromise = new Promise((resolve) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previous === "function") {
        previous();
      }
      resolve();
    };
    if (!document.querySelector('script[data-youtube-iframe-api="true"]')) {
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      script.dataset.youtubeIframeApi = "true";
      document.body.appendChild(script);
    }
  });
  return youtubeApiPromise;
}

function WaveformPlaceholder() {
  return (
    <div className="csmart-audio-analysis-waveform">
      <div className="csmart-audio-analysis-waveformGrid" />
      <div className="csmart-audio-analysis-waveBars" aria-hidden="true">
        {Array.from({ length: 14 }).map((_, index) => (
          <span
            className="csmart-audio-analysis-waveBar"
            key={index}
            style={{ height: `${18 + ((index * 17) % 52)}px` }}
          />
        ))}
      </div>
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="csmart-audio-analysis-skeletonCard" aria-hidden="true">
      <div className="csmart-audio-analysis-skeletonMedia" />
      <div className="csmart-audio-analysis-skeletonBody">
        <span className="csmart-audio-analysis-skeletonLine csmart-audio-analysis-skeletonLineWide" />
        <span className="csmart-audio-analysis-skeletonLine csmart-audio-analysis-skeletonLineMedium" />
        <span className="csmart-audio-analysis-skeletonLine" />
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="csmart-audio-analysis-emptyState">
      <div className="csmart-audio-analysis-emptyIcon">
        <Sparkles size={26} />
      </div>
      <h3 className="csmart-audio-analysis-emptyTitle">
        Start with a URL or an upload
      </h3>
      <p className="csmart-audio-analysis-emptyCopy">
        Jobs appear instantly with processing status, then update when the
        analysis API returns transcript and media metadata.
      </p>
    </div>
  );
}

function AnalysisForm({ onAnalyze, isSubmitting, submitError }) {
  const [url, setUrl] = useState("");
  const [file, setFile] = useState();
  const fileInputRef = useRef(null);
  const canAnalyze = Boolean(url.trim() || file);

  const handleSubmit = () => {
    if (!canAnalyze || isSubmitting) {
      return;
    }
    onAnalyze({
      url: url.trim() || undefined,
      file,
      onComplete: () => {
        setUrl("");
        setFile(undefined);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      },
    });
  };

  return (
    <section
      className="csmart-audio-analysis-formPanel"
      aria-label="Submit media for audio analysis"
    >
      <div className="csmart-audio-analysis-formRow">
        <div className="csmart-audio-analysis-urlField">
          <Link2 size={20} className="csmart-audio-analysis-fieldIcon" />
          <input
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="Paste a video or audio URL"
            className="csmart-audio-analysis-urlInput"
            aria-label="Media URL"
          />
          {url && (
            <button
              type="button"
              onClick={() => setUrl("")}
              className="csmart-audio-analysis-iconButton"
              aria-label="Clear URL"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="video/*,audio/*,.mp4,.mp3,.wav,.m4a,.mov,.webm"
          className="csmart-audio-analysis-hiddenInput"
          onChange={(event) => setFile(event.target.files?.[0])}
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="csmart-audio-analysis-secondaryButton"
        >
          <FileUp size={19} className="csmart-audio-analysis-buttonIcon" />
          <span className="csmart-audio-analysis-truncate">
            {file ? file.name : "Upload file"}
          </span>
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canAnalyze || isSubmitting}
          className="csmart-audio-analysis-primaryButton"
        >
          {isSubmitting ? (
            <Loader2 size={19} className="csmart-audio-analysis-spin" />
          ) : (
            <Play size={18} fill="currentColor" />
          )}
          Analyze
        </button>
      </div>

      {file && (
        <div className="csmart-audio-analysis-fileChip">
          <span className="csmart-audio-analysis-truncate">{file.name}</span>
          <button
            type="button"
            onClick={() => setFile(undefined)}
            className="csmart-audio-analysis-fileRemove"
            aria-label="Remove selected file"
          >
            <X size={15} />
          </button>
        </div>
      )}

      {submitError && (
        <div className="csmart-audio-analysis-errorBanner" role="alert">
          <AlertCircle size={17} />
          {submitError}
        </div>
      )}
    </section>
  );
}

/* ── card helpers ── */
function getPlatform(analysis) {
  const url = analysis.sourceUrl || analysis.video_url || "";
  if (!url || url.startsWith("blob:"))
    return { key: "file", label: "Local file", icon: faFileVideo };
  try {
    const host = new URL(url).hostname.replace(/^www\.|^m\./, "");
    if (host.includes("youtube.com") || host === "youtu.be")
      return { key: "youtube", label: "YouTube", icon: faYoutube };
    if (host.includes("instagram.com"))
      return { key: "instagram", label: "Instagram", icon: faInstagram };
    if (host.includes("tiktok.com"))
      return { key: "tiktok", label: "TikTok", icon: faTiktok };
    if (host.includes("twitter.com") || host.includes("x.com"))
      return { key: "twitter", label: "X / Twitter", icon: faXTwitter };
    if (host.includes("facebook.com") || host.includes("fb.com"))
      return { key: "facebook", label: "Facebook", icon: faFacebook };
    return { key: "web", label: host, icon: faGlobe };
  } catch {
    return { key: "web", label: "Web", icon: faGlobe };
  }
}

function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function getCardStats(analysis) {
  const transcript = analysis.transcript || [];
  const segmentCount = transcript.length;
  const durationSec = transcript.length
    ? Math.max(...transcript.map((s) => s.end || 0))
    : null;
  return { segmentCount, durationSec };
}

function AnalysisCard({ analysis, onSelect }) {
  const [imageFailed, setImageFailed] = useState(false);
  const status = statusConfig[analysis.status] || statusConfig.processing;
  const StatusIcon = status.icon;
  const progress = Math.min(
    100,
    Math.max(
      analysis.progress ?? (analysis.status === "completed" ? 100 : 32),
      0,
    ),
  );
  const thumbnail = imageFailed ? undefined : youtubeThumbnailUrl(analysis);
  const platform = getPlatform(analysis);
  const { segmentCount, durationSec } = getCardStats(analysis);
  const duration = formatDuration(durationSec);

  return (
    <button
      type="button"
      onClick={() => onSelect(analysis.id)}
      className="csmart-audio-analysis-jobCard"
    >
      <div className="csmart-audio-analysis-cardMedia">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt=""
            loading="lazy"
            className="csmart-audio-analysis-cardImage"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <WaveformPlaceholder />
        )}

        <div className="csmart-audio-analysis-cardShade" />

        <div className="csmart-audio-analysis-playBadge">
          <FontAwesomeIcon icon={faPlay} />
        </div>

        {duration && (
          <div className="csmart-audio-analysis-durationChip">
            <FontAwesomeIcon icon={faClock} />
            {duration}
          </div>
        )}

        <div
          className={`csmart-audio-analysis-statusBadge csmart-audio-analysis-statusBadge-${analysis.status}`}
        >
          <StatusIcon size={13} />
          {status.label}
        </div>
      </div>

      <div className="csmart-audio-analysis-cardBody">
        <div className="csmart-audio-analysis-cardBodyTop">
          <h2 className="csmart-audio-analysis-cardTitle">{analysis.title}</h2>

          <div className="csmart-audio-analysis-cardMeta">
            <span
              className={`csmart-audio-analysis-platformPill csmart-audio-analysis-platformPill-${platform.key}`}
            >
              <FontAwesomeIcon icon={platform.icon} />
              {platform.label}
            </span>

            <span className="csmart-audio-analysis-metaPill">
              <FontAwesomeIcon icon={faLanguage} />
              {analysis.language && analysis.language !== "Detecting"
                ? analysis.language.toUpperCase()
                : "Detecting"}
            </span>

            {analysis.uploader && (
              <span className="csmart-audio-analysis-metaPill">
                <FontAwesomeIcon icon={faCircleUser} />
                <span className="csmart-audio-analysis-truncate">
                  {analysis.uploader}
                </span>
              </span>
            )}

            {segmentCount > 0 && (
              <span className="csmart-audio-analysis-metaPill">
                <FontAwesomeIcon icon={faMessage} />
                {segmentCount} {segmentCount === 1 ? "segment" : "segments"}
              </span>
            )}
          </div>

          <div className="csmart-audio-analysis-cardDateRow">
            <FontAwesomeIcon icon={faCalendar} />
            {formatDateTime(analysis.createdAt)}
          </div>
        </div>

        <div className="csmart-audio-analysis-cardFooter">
          {analysis.status === "processing" && (
            <div className="csmart-audio-analysis-progressBlock">
              <div className="csmart-audio-analysis-progressCopy">
                <span>
                  <FontAwesomeIcon
                    icon={faMicrochip}
                    style={{ marginRight: 5 }}
                  />
                  Pipeline progress
                </span>
                <span>{progress}%</span>
              </div>

              <div className="csmart-audio-analysis-progressTrack">
                <div
                  className="csmart-audio-analysis-progressFill"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {analysis.status === "failed" && (
            <p className="csmart-audio-analysis-cardError csmart-audio-analysis-truncate">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                style={{ marginRight: 5 }}
              />
              {analysis.error || "Analysis failed"}
            </p>
          )}

          {analysis.status === "completed" && (
            <span className="csmart-audio-analysis-readyHint">
              <FontAwesomeIcon icon={faCircleCheck} />
              Ready to view
              {duration && (
                <span className="csmart-audio-analysis-readyDuration">
                  &nbsp;·&nbsp;{duration}
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

const VideoPlayer = forwardRef(function VideoPlayer(
  { analysis, onTimeUpdate },
  ref,
) {
  const embedUrl = youtubeEmbedUrl(analysis.video_url);
  const audioOnly = isAudioUrl(analysis.video_url);
  const canUseNative = Boolean(analysis.video_url && !embedUrl);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const iframeRef = useRef(null);
  const youtubePlayerRef = useRef(null);
  const [youtubeReady, setYoutubeReady] = useState(false);

  useEffect(() => {
    if (!embedUrl || !iframeRef.current) {
      return undefined;
    }

    let cancelled = false;
    let pollHandle;

    loadYouTubeIframeAPI().then(() => {
      if (cancelled || !iframeRef.current || !window.YT) {
        return;
      }

      youtubePlayerRef.current = new window.YT.Player(iframeRef.current, {
        events: {
          onReady: () => {
            if (cancelled) {
              return;
            }
            setYoutubeReady(true);
            if (onTimeUpdate) {
              pollHandle = window.setInterval(() => {
                const player = youtubePlayerRef.current;
                if (!player) {
                  return;
                }
                try {
                  onTimeUpdate(player.getCurrentTime());
                } catch {
                  /* The iframe API can briefly be unavailable while changing videos. */
                }
              }, 250);
            }
          },
        },
      });
    });

    return () => {
      cancelled = true;
      if (pollHandle) {
        window.clearInterval(pollHandle);
      }
      try {
        youtubePlayerRef.current?.destroy();
      } catch {
        /* Ignore YouTube teardown races. */
      }
      youtubePlayerRef.current = null;
      setYoutubeReady(false);
    };
  }, [embedUrl, onTimeUpdate]);

  useImperativeHandle(
    ref,
    () => ({
      seekTo: (time) => {
        if (videoRef.current) {
          videoRef.current.currentTime = time;
          void videoRef.current.play().catch(() => undefined);
          return;
        }
        if (audioRef.current) {
          audioRef.current.currentTime = time;
          void audioRef.current.play().catch(() => undefined);
          return;
        }
        if (youtubePlayerRef.current && youtubeReady) {
          try {
            youtubePlayerRef.current.seekTo(time, true);
            youtubePlayerRef.current.playVideo();
          } catch {
            /* Ignore iframe API seek failures. */
          }
        }
      },
    }),
    [youtubeReady],
  );

  if (canUseNative && audioOnly) {
    return (
      <div className="csmart-audio-analysis-audioPlayerShell">
        <div className="csmart-audio-analysis-audioPlayerInner">
          <div className="csmart-audio-analysis-audioIcon">
            <FileAudio2 size={42} />
          </div>
          <audio
            ref={audioRef}
            controls
            className="csmart-audio-analysis-nativeAudio"
            src={analysis.video_url}
            onTimeUpdate={(event) =>
              onTimeUpdate?.(event.currentTarget.currentTime)
            }
          />
        </div>
      </div>
    );
  }

  if (canUseNative) {
    return (
      <video
        ref={videoRef}
        className="csmart-audio-analysis-nativeVideo"
        src={analysis.video_url}
        poster={analysis.thumbnail}
        controls
        playsInline
        onTimeUpdate={(event) =>
          onTimeUpdate?.(event.currentTarget.currentTime)
        }
      />
    );
  }

  if (embedUrl) {
    const apiUrl = embedUrl.includes("enablejsapi=")
      ? embedUrl
      : `${embedUrl}${embedUrl.includes("?") ? "&" : "?"}enablejsapi=1`;

    return (
      <iframe
        ref={iframeRef}
        className="csmart-audio-analysis-youtubeFrame"
        src={apiUrl}
        title={analysis.title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  }

  return (
    <div className="csmart-audio-analysis-mediaPending">
      <FileVideo2
        size={42}
        className="csmart-audio-analysis-mediaPendingIcon"
      />
      <p>Media preview will appear when the API returns a playable URL.</p>
    </div>
  );
});

function TranscriptPanel({ transcript, onSeek, activeTime = 0 }) {
  if (!transcript.length) {
    return (
      <div className="csmart-audio-analysis-transcriptPending">
        <MessageSquareText
          size={34}
          className="csmart-audio-analysis-transcriptPendingIcon"
        />
        <h3>Transcript pending</h3>
        <p>
          The transcript will appear here as speaker segments when the analysis
          completes.
        </p>
      </div>
    );
  }

  return (
    <div className="csmart-audio-analysis-transcriptPanel">
      <div className="csmart-audio-analysis-transcriptHeader">
        <div>
          <h2>Transcription</h2>
          <p>{transcript.length} speaker-wise segments</p>
        </div>
        <div className="csmart-audio-analysis-syncBadge">SYNC</div>
      </div>

      <div className="csmart-audio-analysis-transcriptList">
        {transcript.map((segment, index) => {
          const isActive =
            activeTime >= segment.start && activeTime <= segment.end;

          return (
            <button
              key={`${segment.speaker}-${segment.start}-${index}`}
              type="button"
              onClick={() => onSeek(segment.start)}
              className={`csmart-audio-analysis-transcriptSegment ${
                isActive ? "csmart-audio-analysis-transcriptSegmentActive" : ""
              }`}
            >
              <div className="csmart-audio-analysis-segmentMeta">
                <span className="csmart-audio-analysis-timeChip">
                  {formatTimestamp(segment.start)} –{" "}
                  {formatTimestamp(segment.end)}
                </span>
                <span className="csmart-audio-analysis-speakerChip">
                  {segment.speaker}
                </span>
              </div>
              <p>{segment.text}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AnalysisDetail({ analysis, isRefreshing, hasFetchError, onBack }) {
  const playerRef = useRef(null);
  const [activeTime, setActiveTime] = useState(0);

  useEffect(() => {
    setActiveTime(0);
  }, [analysis.id]);

  const handleSeek = useCallback((start) => {
    playerRef.current?.seekTo(start);
  }, []);

  return (
    <div className="csmart-audio-analysis-detailView">
      <div className="csmart-audio-analysis-detailTopbar">
        <button
          type="button"
          onClick={onBack}
          className="csmart-audio-analysis-backButton"
        >
          <ArrowLeft size={18} />
          Analysis jobs
        </button>
        <div className="csmart-audio-analysis-detailBadges">
          {isRefreshing && (
            <span className="csmart-audio-analysis-refreshBadge">
              <Loader2 size={14} className="csmart-audio-analysis-spin" />
              Refreshing
            </span>
          )}
          <span
            className={`csmart-audio-analysis-detailStatus csmart-audio-analysis-detailStatus-${analysis.status}`}
          >
            {analysis.status}
          </span>
        </div>
      </div>

      {hasFetchError && (
        <div className="csmart-audio-analysis-warningBanner">
          Could not fetch the detail endpoint yet. Showing locally available job
          data.
        </div>
      )}

      <div className="csmart-audio-analysis-detailGrid">
        <section className="csmart-audio-analysis-mediaColumn">
          <VideoPlayer
            ref={playerRef}
            analysis={analysis}
            onTimeUpdate={setActiveTime}
          />
          <div className="csmart-audio-analysis-detailInfo">
            <h1>{analysis.title}</h1>
            <div className="csmart-audio-analysis-detailMeta">
              <span>
                <Languages size={16} />
                {analysis.language || "Detecting"}
              </span>
              <span>
                <UserRound size={16} />
                {analysis.uploader || "Unknown uploader"}
              </span>
              <span>{formatDateTime(analysis.createdAt)}</span>
            </div>
          </div>
        </section>

        <aside className="csmart-audio-analysis-transcriptColumn">
          <TranscriptPanel
            transcript={analysis.transcript || []}
            onSeek={handleSeek}
            activeTime={activeTime}
          />
        </aside>
      </div>
    </div>
  );
}

export function AudioAnalysisModule({
  apiBaseUrl = "",
  persistKey = DEFAULT_PERSIST_KEY,
  title = "Audio Analysis",
  subtitle = "Upload or link audio/video media to generate synchronized speaker transcripts.",
}) {
  const initialStateRef = useRef(null);
  if (!initialStateRef.current) {
    initialStateRef.current = safeParseStoredState(persistKey);
  }

  const [analyses, setAnalyses] = useState(initialStateRef.current.analyses);
  const [details, setDetails] = useState(initialStateRef.current.details);
  const [selectedId, setSelectedId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [refreshingIds, setRefreshingIds] = useState({});
  const [fetchErrors, setFetchErrors] = useState({});

  const processing = useMemo(
    () => analyses.filter((analysis) => analysis.status === "processing"),
    [analyses],
  );
  const selectedSummary = selectedId
    ? analyses.find((analysis) => analysis.id === selectedId)
    : undefined;
  const selectedAnalysis = selectedSummary
    ? details[selectedSummary.id] || { ...selectedSummary, transcript: [] }
    : undefined;
  const processingKey = processing.map((analysis) => analysis.id).join(",");
  const processingRef = useRef(processing);
  processingRef.current = processing;

  useEffect(() => {
    saveStoredState(persistKey, analyses, details);
  }, [analyses, details, persistKey]);

  useEffect(() => {
    if (!processing.length) {
      return undefined;
    }
    const interval = window.setInterval(() => {
      setAnalyses((current) =>
        current.map((item) =>
          item.status === "processing"
            ? {
                ...item,
                progress: Math.min(
                  92,
                  (item.progress || 20) +
                    Math.max(1, Math.round(Math.random() * 5)),
                ),
              }
            : item,
        ),
      );
    }, 2500);
    return () => window.clearInterval(interval);
  }, [processing.length]);

  const mergeDetail = useCallback((detail) => {
    setDetails((current) => ({ ...current, [detail.id]: detail }));
    setAnalyses((current) =>
      current.map((item) =>
        item.id === detail.id ? { ...item, ...detail } : item,
      ),
    );
  }, []);

  const refreshProcessing = useCallback(async () => {
    const current = processingRef.current;
    if (!current.length) {
      return;
    }
    current.forEach((analysis) => {
      setRefreshingIds((prev) => ({ ...prev, [analysis.id]: true }));
    });
    const results = await Promise.allSettled(
      current.map((analysis) => getAnalysis(apiBaseUrl, analysis.id, analysis)),
    );
    results.forEach((result, index) => {
      const analysis = current[index];
      setRefreshingIds((prev) => ({ ...prev, [analysis.id]: false }));
      if (result.status === "fulfilled") {
        mergeDetail(result.value);
        setFetchErrors((prev) => ({ ...prev, [analysis.id]: "" }));
      } else {
        setFetchErrors((prev) => ({
          ...prev,
          [analysis.id]:
            result.reason instanceof Error
              ? result.reason.message
              : "Could not refresh analysis",
        }));
      }
    });
  }, [apiBaseUrl, mergeDetail]);

  useEffect(() => {
    if (!processingKey) {
      return undefined;
    }
    refreshProcessing();
    const interval = window.setInterval(refreshProcessing, 4000);
    return () => window.clearInterval(interval);
  }, [processingKey, refreshProcessing]);

  const handleAnalyze = useCallback(
    async ({ url, file, onComplete }) => {
      if (isSubmitting) {
        return;
      }

      const temporaryId = createClientId();
      const localVideoUrl = file ? URL.createObjectURL(file) : undefined;
      const input = { url, file, localVideoUrl };
      const titleFromUrl = (() => {
        if (!url) {
          return undefined;
        }
        try {
          return new URL(url).hostname.replace(/^www\./, "");
        } catch {
          return url;
        }
      })();

      const existing = {
        id: temporaryId,
        title:
          file?.name?.replace(/\.[^.]+$/, "") || titleFromUrl || "New analysis",
        language: "Detecting",
        status: "processing",
        video_url: localVideoUrl || url,
        sourceUrl: url,
        createdAt: nowIso(),
        progress: progressSeed(),
      };

      setSubmitError("");
      setIsSubmitting(true);
      setAnalyses((current) => [existing, ...current]);

      try {
        const rawResult = await analyzeMediaSSE(input, {
          onPing: () => {
            setAnalyses((current) =>
              current.map((item) =>
                item.id === temporaryId
                  ? {
                      ...item,
                      progress: Math.min(88, (item.progress || 20) + 4),
                    }
                  : item,
              ),
            );
          },
        });

        const result = normalizeAnalysis(
          rawResult,
          temporaryId,
          input,
          existing,
        );

        setAnalyses((current) => {
          const withoutTemporary = current.filter(
            (item) => item.id !== temporaryId && item.id !== result.summary.id,
          );
          return [result.summary, ...withoutTemporary];
        });
        if (result.detail) {
          setDetails((current) => {
            const next = { ...current };
            delete next[temporaryId];
            next[result.detail.id] = result.detail;
            return next;
          });
        }
        onComplete?.();
      } catch (error) {
        if (error?.name === "AbortError") return;
        const message =
          error instanceof Error ? error.message : "Analysis failed";
        setSubmitError(message);
        setAnalyses((current) =>
          current.map((item) =>
            item.id === temporaryId
              ? {
                  ...item,
                  status: "failed",
                  progress: item.progress,
                  error: message,
                }
              : item,
          ),
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting],
  );

  const refreshSelected = useCallback(async () => {
    if (!selectedSummary) {
      return;
    }
    setRefreshingIds((current) => ({ ...current, [selectedSummary.id]: true }));
    try {
      const detail = await getAnalysis(
        apiBaseUrl,
        selectedSummary.id,
        selectedSummary,
      );
      mergeDetail(detail);
      setFetchErrors((current) => ({ ...current, [selectedSummary.id]: "" }));
    } catch (error) {
      setFetchErrors((current) => ({
        ...current,
        [selectedSummary.id]:
          error instanceof Error ? error.message : "Could not refresh analysis",
      }));
    } finally {
      setRefreshingIds((current) => ({
        ...current,
        [selectedSummary.id]: false,
      }));
    }
  }, [apiBaseUrl, mergeDetail, selectedSummary]);

  useEffect(() => {
    if (selectedSummary && !details[selectedSummary.id]) {
      refreshSelected();
    }
  }, [details, refreshSelected, selectedSummary]);

  if (selectedAnalysis) {
    return (
      <main className="csmart-audio-analysis-shell">
        <div className="csmart-audio-analysis-wrap">
          <AnalysisDetail
            analysis={selectedAnalysis}
            isRefreshing={Boolean(refreshingIds[selectedAnalysis.id])}
            hasFetchError={
              Boolean(fetchErrors[selectedAnalysis.id]) &&
              !details[selectedAnalysis.id]
            }
            onBack={() => setSelectedId(null)}
          />
        </div>
      </main>
    );
  }

  return (
    <main className="csmart-audio-analysis-shell">
      <div className="csmart-audio-analysis-wrap">
        <header className="csmart-audio-analysis-header">
          <div className="csmart-audio-analysis-headingBlock">
            <div className="csmart-audio-analysis-eyebrow">
              <Sparkles size={14} />
              AI audio/video analysis
            </div>
            <h1 className="csmart-audio-analysis-title">{title}</h1>
            <p className="csmart-audio-analysis-subtitle">{subtitle}</p>
          </div>

          <div className="csmart-audio-analysis-statsRow">
            <div className="csmart-audio-analysis-statCard">
              <div className="csmart-audio-analysis-statIcon">
                <Activity size={20} />
              </div>
              <div>
                <div className="csmart-audio-analysis-statLabel">
                  Active jobs
                </div>
                <div className="csmart-audio-analysis-statValue">
                  {processing.length}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={refreshProcessing}
              className="csmart-audio-analysis-refreshButton"
            >
              <RefreshCw size={17} />
              Refresh
            </button>
          </div>
        </header>

        <AnalysisForm
          onAnalyze={handleAnalyze}
          isSubmitting={isSubmitting}
          submitError={submitError}
        />

        <section className="csmart-audio-analysis-jobsSection">
          <div className="csmart-audio-analysis-sectionHeader">
            <div>
              <h2>Analysis Jobs</h2>
              <p>
                {analyses.length
                  ? `${analyses.length} total analyses`
                  : "No analyses submitted yet"}
              </p>
            </div>
            {processing.length > 0 && (
              <div className="csmart-audio-analysis-pollingBadge">
                <Loader2 size={14} className="csmart-audio-analysis-spin" />
                Polling status
              </div>
            )}
          </div>

          {analyses.length ? (
            <div className="csmart-audio-analysis-cardGrid">
              {analyses.map((analysis) => (
                <AnalysisCard
                  key={analysis.id}
                  analysis={analysis}
                  onSelect={setSelectedId}
                />
              ))}
            </div>
          ) : (
            <div className="csmart-audio-analysis-emptyGrid">
              <EmptyState />
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function AudioAnalysisMain(props) {
  return (
    <div className="container">
      <NavBar />
      <AudioAnalysisModule apiBaseUrl={modelsProxyUrl()} {...props} />
      <ReactProSidebar />
    </div>
  );
}

export default AudioAnalysisMain;
